// Alpine Sleep Pro: 3D control unit for the hero, modelled 1:1 after the product photos.
// Landscape box, round radial fan on the long front face, louvred grille on the short side,
// water funnel at the back-left of the top, glass control panel with two zone displays.
// Progressive enhancement: the product photo returns when three.js or WebGL is unavailable.
const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';

const ICE = 0x8ec5ff;
const EMBER = 0xff9a6b;

// Real proportions from the photos: length 1.6, depth 0.95, height 1.0 (scene units).
const L = 1.6, D = 0.95, H = 1.0, R = 0.07;

function roundedRect(THREE, w, d, r) {
  const s = new THREE.Shape();
  const x = -w / 2, y = -d / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + d - r);
  s.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
  s.lineTo(x + r, y + d);
  s.quadraticCurveTo(x, y + d, x, y + d - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

// Top glass panel: canvas top edge = back of the unit, bottom edge = front.
function topTexture(THREE, left, right) {
  const W = 2048, Hh = Math.round(2048 * (D / L));
  const c = document.createElement('canvas');
  c.width = W; c.height = Hh;
  const g = c.getContext('2d');
  // v() takes depth measured from the back; the plane maps canvas rows front-to-back, so mirror it.
  const u = (x) => x * W, v = (y) => (1 - y) * Hh;

  // Positions measured on the product photo, as fractions of length (u, left to right)
  // and of depth (v, back to front).
  g.fillStyle = '#0b0c0e';
  g.fillRect(0, 0, W, Hh);
  // Tank lid: the whole left third over the full depth, matte, split from the glass by one seam.
  g.fillStyle = '#121316';
  g.fillRect(0, 0, u(0.38), Hh);
  g.fillStyle = '#040506';
  g.fillRect(u(0.38), 0, 6, Hh);
  // Finger notch in the lid edge, centred on the seam (packshot photo).
  g.beginPath(); g.arc(u(0.38), v(0.5), 46, Math.PI / 2, -Math.PI / 2); g.fill();

  // Funnel recess in the middle of the lid (the 3D bowl sits on top of this).
  g.fillStyle = '#030304';
  g.beginPath(); g.ellipse(u(0.22), v(0.58), u(0.1), v(0.17), 0, 0, Math.PI * 2); g.fill();
  // Two small holes on the funnel axis, one near the back edge, one near the front edge.
  g.fillStyle = '#000';
  [[0.23, 0.11], [0.25, 0.93]].forEach(([x, y]) => { g.beginPath(); g.arc(u(x), v(y), 10, 0, Math.PI * 2); g.fill(); });

  // Brand oval just right of the seam.
  g.strokeStyle = 'rgba(238,241,245,0.6)';
  g.lineWidth = 4;
  g.beginPath(); g.ellipse(u(0.49), v(0.55), 34, 64, 0, 0, Math.PI * 2); g.stroke();
  // Small square indicator.
  g.strokeRect(u(0.667) - 14, v(0.57) - 14, 28, 28);

  // Two zone displays one behind the other (depth axis), each with its icon row on the right.
  const lcd = (cy, text, tint) => {
    const cx = u(0.735), w = 120, h = 250;
    g.fillStyle = '#15171b';
    g.fillRect(cx - w / 2, cy - h / 2, w, h);
    g.strokeStyle = 'rgba(238,241,245,0.3)';
    g.lineWidth = 3;
    g.strokeRect(cx - w / 2, cy - h / 2, w, h);
    g.save();
    g.translate(cx, cy);
    g.rotate(-Math.PI / 2);
    g.font = '700 70px "Space Grotesk", system-ui, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = tint;
    g.fillText(text, 0, 4);
    g.restore();
    g.strokeStyle = 'rgba(238,241,245,0.7)';
    g.lineWidth = 4;
    for (let i = 0; i < 4; i++) {
      g.beginPath(); g.arc(u(0.835), cy - 135 + i * 90, 17, 0, Math.PI * 2); g.stroke();
    }
  };
  lcd(v(0.73), left, '#cfe6ff');
  lcd(v(0.34), right, '#ffd9c7');

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

function shadowTexture(THREE) {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(128, 128, 8, 128, 128, 128);
  grad.addColorStop(0, 'rgba(0,0,0,0.8)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(c);
}

function buildDevice(THREE, opts) {
  const unit = new THREE.Group();
  const shell = new THREE.MeshPhysicalMaterial({ color: 0x111215, roughness: 0.62, metalness: 0.05, clearcoat: 0.15, clearcoatRoughness: 0.6 });
  const recess = new THREE.MeshStandardMaterial({ color: 0x07080a, roughness: 0.9 });
  const plastic = new THREE.MeshStandardMaterial({ color: 0x141518, roughness: 0.55 });

  // Body.
  const bodyGeo = new THREE.ExtrudeGeometry(roundedRect(THREE, L, D, R), { depth: H, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelSegments: 5, curveSegments: 14 });
  bodyGeo.rotateX(-Math.PI / 2);
  unit.add(new THREE.Mesh(bodyGeo, shell));

  // Top glass control panel.
  const top = new THREE.Mesh(
    new THREE.PlaneGeometry(L - 0.04, D - 0.04),
    new THREE.MeshPhysicalMaterial({ map: topTexture(THREE, opts.left, opts.right), roughness: 0.12, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.05 })
  );
  top.rotation.x = -Math.PI / 2;
  top.position.y = H + 0.021;
  unit.add(top);

  // Water funnel: recessed bowl with a light filter cap, back-left of the top.
  const fx = -L / 2 + 0.22 * L, fz = -D / 2 + 0.58 * D;
  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.06, 0.09, 48, 1, true), new THREE.MeshStandardMaterial({ color: 0x050506, roughness: 0.4, side: THREE.BackSide }));
  bowl.scale.z = 0.95;
  bowl.position.set(fx, H - 0.025, fz);
  unit.add(bowl);
  const filter = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.065, 0.014, 40), new THREE.MeshStandardMaterial({ color: 0xdfe3e8, roughness: 0.3, metalness: 0.5 }));
  filter.position.set(fx, H - 0.045, fz);
  unit.add(filter);
  // Soft-edged rim so the funnel reads against the black top, as in the photo.
  const funnelRim = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.008, 10, 64), new THREE.MeshStandardMaterial({ color: 0x2a2d33, roughness: 0.35, metalness: 0.3 }));
  funnelRim.rotation.x = Math.PI / 2;
  funnelRim.position.set(fx, H + 0.022, fz);
  unit.add(funnelRim);

  // Round fan with radial fins around a hub. Both long faces carry one (front: product photo,
  // back: packshot photo), centred at about 52 % of the length and 48 % of the height.
  const fanR = 0.31;
  const finGeo = new THREE.BoxGeometry(fanR - 0.06, 0.008, 0.03);
  const makeFan = () => {
    const fan = new THREE.Group();
    fan.add(new THREE.Mesh(new THREE.CircleGeometry(fanR, 64), recess));
    fan.add(new THREE.Mesh(new THREE.TorusGeometry(fanR, 0.012, 12, 80), plastic));
    for (let i = 0; i < 44; i++) {
      const a = (i / 44) * Math.PI * 2;
      const fin = new THREE.Mesh(finGeo, plastic);
      fin.position.set(Math.cos(a) * (fanR / 2 + 0.025), Math.sin(a) * (fanR / 2 + 0.025), 0.012);
      fin.rotation.z = a + 0.18;
      fan.add(fin);
    }
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.03, 40), plastic);
    hub.rotation.x = Math.PI / 2;
    hub.position.z = 0.02;
    fan.add(hub);
    return fan;
  };
  const frontFan = makeFan();
  frontFan.position.set(-L / 2 + 0.52 * L, 0.48, D / 2 + 0.021);
  unit.add(frontFan);
  const backFan = makeFan();
  backFan.rotation.y = Math.PI;
  backFan.position.set(L / 2 - 0.52 * L, 0.48, -D / 2 - 0.021);
  unit.add(backFan);

  // Connection end (short left side, office photo): recessed panel with two hose fittings and a power inlet.
  const ports = new THREE.Group();
  ports.add(new THREE.Mesh(new THREE.PlaneGeometry(D * 0.42, H * 0.4), recess));
  const fitting = new THREE.MeshStandardMaterial({ color: 0xd9dde3, roughness: 0.4, metalness: 0.3 });
  [-0.09, 0.09].forEach((x) => {
    const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.05, 32), fitting);
    collar.rotation.x = Math.PI / 2;
    collar.position.set(x, 0.05, 0.025);
    ports.add(collar);
    const nozzle = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.026, 0.08, 24), fitting);
    nozzle.rotation.x = Math.PI / 2;
    nozzle.position.set(x, 0.05, 0.07);
    ports.add(nozzle);
  });
  const inlet = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 0.02), plastic);
  inlet.position.set(0, -0.11, 0.01);
  ports.add(inlet);
  ports.rotation.y = -Math.PI / 2;
  ports.position.set(-L / 2 - 0.021, 0.38, 0);
  unit.add(ports);

  // Louvred grille on the short right face.
  const grille = new THREE.Group();
  // Measured: 60 % of the depth wide, 85 % of the height tall, centred in depth, four rows of vertical slats.
  const gw = D * 0.6, gh = H * 0.85;
  grille.add(new THREE.Mesh(new THREE.PlaneGeometry(gw, gh), recess));
  const rows = 4, cols = 14;
  const slatGeo = new THREE.BoxGeometry(0.011, gh / rows - 0.03, 0.025);
  for (let row = 0; row < rows; row++) {
    for (let i = 0; i < cols; i++) {
      const s = new THREE.Mesh(slatGeo, plastic);
      s.position.set(-gw / 2 + 0.025 + i * ((gw - 0.05) / (cols - 1)), -gh / 2 + (row + 0.5) * (gh / rows), 0.012);
      grille.add(s);
    }
  }
  const frame = new THREE.Mesh(new THREE.BoxGeometry(gw + 0.02, gh + 0.02, 0.006), plastic);
  frame.position.z = -0.004;
  grille.add(frame);
  grille.rotation.y = Math.PI / 2;
  grille.position.set(L / 2 + 0.021, 0.5, 0);
  unit.add(grille);

  // Recessed base line.
  const base = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedRect(THREE, L - 0.06, D - 0.06, R), { depth: 0.04, bevelEnabled: false }), recess);
  base.rotation.x = -Math.PI / 2;
  base.position.y = -0.03;
  unit.add(base);

  unit.position.y = -H / 2;
  const pivot = new THREE.Group();
  pivot.add(unit);
  return pivot;
}

async function mount(stage) {
  if (stage.dataset.as3d) return;
  stage.dataset.as3d = '1';
  const host = stage.querySelector('[data-hero-3d]');
  if (!host) return;
  const fail = () => stage.classList.add('is-3d-failed');
  const timeout = setTimeout(() => { if (!stage.classList.contains('is-3d-ready')) fail(); }, 12000);

  let THREE;
  try {
    THREE = await import(THREE_URL);
  } catch {
    clearTimeout(timeout);
    return fail();
  }

  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch {
    clearTimeout(timeout);
    return fail();
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;
  host.appendChild(canvas);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(26, 16 / 9, 0.1, 60);
  const target = new THREE.Vector3(0, -0.02, 0);

  // Studio light: soft top key, cool rim left, warm rim right (one per zone).
  scene.add(new THREE.HemisphereLight(0xaab8cc, 0x050608, 0.45));
  const key = new THREE.SpotLight(0xffffff, 60, 14, Math.PI / 6, 0.6, 1.5);
  key.position.set(0.6, 4.2, 2.2);
  key.target.position.copy(target);
  scene.add(key, key.target);
  const rimIce = new THREE.PointLight(ICE, 34, 12, 1.6);
  rimIce.position.set(-3, 1.2, -1.4);
  scene.add(rimIce);
  const rimEmber = new THREE.PointLight(EMBER, 34, 12, 1.6);
  rimEmber.position.set(3, 1.2, -1.4);
  scene.add(rimEmber);
  const front = new THREE.PointLight(0xdfe7f2, 5, 10, 2);
  front.position.set(0, 0.6, 4);
  scene.add(front);

  const pivot = buildDevice(THREE, { left: stage.dataset.left || '17°', right: stage.dataset.right || '29°' });
  scene.add(pivot);

  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 2.4), new THREE.MeshBasicMaterial({ map: shadowTexture(THREE), transparent: true, depthWrite: false }));
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = -H / 2 - 0.02;
  scene.add(shadow);

  const resize = () => {
    const { width, height } = host.getBoundingClientRect();
    if (!width || !height) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    // Keep the whole unit in frame on narrow screens.
    const dist = camera.aspect < 1 ? 6.4 : camera.aspect < 1.4 ? 5.4 : 4.6;
    // Elevation matches the product photo (~32°), so the top panel reads like in the shot.
    camera.position.set(dist * 0.4, dist * 0.53, dist * 0.75);
    camera.lookAt(target);
    camera.updateProjectionMatrix();
  };
  resize();
  new ResizeObserver(resize).observe(host);

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pointer = { x: 0, y: 0 };
  stage.addEventListener('pointermove', (e) => {
    const r = stage.getBoundingClientRect();
    pointer.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    pointer.y = ((e.clientY - r.top) / r.height) * 2 - 1;
  });
  stage.addEventListener('pointerleave', () => { pointer.x = 0; pointer.y = 0; });

  let visible = true;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) loop(); }).observe(stage);

  const start = performance.now();
  let raf = 0;
  let turn = 0;
  let smoothX = 0;
  const ease = (t) => 1 - Math.pow(1 - t, 3);

  function frame(now) {
    const t = (now - start) / 1000;
    const intro = reduce ? 1 : ease(Math.min(t / 1.8, 1));
    // Scroll progress of the stage (0 in view, 1 scrolled away) turns the unit like a product reveal.
    const r = stage.getBoundingClientRect();
    const scroll = reduce ? 0 : Math.min(Math.max(-r.top / (r.height || 1), 0), 1);
    // All four sides are now documented by photos, so the unit turns a full 360°:
    // slow turntable spin, plus a quarter turn while the hero scrolls away, plus pointer nudge.
    turn += reduce ? 0 : 0.0028;
    smoothX += (pointer.x - smoothX) * 0.06;
    pivot.rotation.y = -0.2 + (1 - intro) * -0.6 + turn + scroll * 1.6 + smoothX * 0.3;
    pivot.rotation.x += ((pointer.y * 0.06) - pivot.rotation.x) * 0.07;
    pivot.position.y = (1 - intro) * -0.35 + (reduce ? 0 : Math.sin(t * 0.8) * 0.018);
    shadow.material.opacity = 0.5 + intro * 0.5;
    renderer.render(scene, camera);
  }

  function loop() {
    cancelAnimationFrame(raf);
    const tick = (now) => {
      frame(now);
      if (visible && !reduce) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  }

  if (reduce) stage.addEventListener('pointermove', () => requestAnimationFrame(frame));
  loop();
  clearTimeout(timeout);
  stage.classList.remove('is-3d-failed');
  stage.classList.add('is-3d-ready');
}

function init(scope = document) {
  scope.querySelectorAll('[data-hero-3d-stage]').forEach((stage) => {
    const go = () => mount(stage);
    'requestIdleCallback' in window ? requestIdleCallback(go, { timeout: 1500 }) : setTimeout(go, 300);
  });
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
